import { useCallback, useState } from "react";

import { base64toBlob } from "../utils/base64";
import { cropImageToSquare } from "../utils/image";

interface PhotoUploadResponse {
  id: string | null;
}

interface UsePhotoUpload {
  photo?: string;
  isLoading: boolean;
  reset: () => void;
  storePhoto: (dataURL: string, minSize: number) => Promise<void>;
  uploadPhoto: (dataURL: string) => Promise<PhotoUploadResponse>;
  setPhoto: (url: string) => void;
  hasChanged: boolean;
}

export const SEASON_PASS_UPLOAD_IMAGE_URI = `${process.env.NEXT_PUBLIC_SARKANNIEMI_API_URI}/v1/ecommerce/season-pass/image`;

const usePhotoUpload = (): UsePhotoUpload => {
  const [photo, setPhoto] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState(false);
  const [hasChanged, setChanged] = useState(false);

  const reset = useCallback(() => {
    setPhoto(undefined);
    setChanged(false);
  }, []);

  const storePhoto = useCallback(
    async (dataURL: string, minSize: number): Promise<void> => {
      setLoading(true);
      setChanged(true);
      try {
        const croppedPhoto = await cropImageToSquare(dataURL, minSize, 500);
        setPhoto(croppedPhoto);
        setLoading(false);
        return Promise.resolve();
      } catch (error) {
        setPhoto(undefined);
        setLoading(false);
        return Promise.reject(error);
      }
    },
    [],
  );

  const uploadPhoto = useCallback(
    async (dataURL: string): Promise<PhotoUploadResponse> => {
      setLoading(true);
      setChanged(true);
      try {
        const parts = dataURL.split(",");
        const mime = parts[0].substring(
          parts[0].indexOf(":") + 1,
          parts[0].indexOf(";"),
        );

        const formData = new FormData();
        formData.append("image", base64toBlob(parts[1], mime));

        const response = await fetch(SEASON_PASS_UPLOAD_IMAGE_URI, {
          method: "POST",
          body: formData,
        });

        setLoading(false);
        return response.status === 200
          ? response.json()
          : Promise.reject("error.imageUploadFailed");
      } catch (e) {
        setLoading(false);
        return Promise.reject("error.imageUploadFailed");
      }
    },
    [],
  );

  return {
    photo,
    isLoading,
    reset,
    storePhoto,
    uploadPhoto,
    setPhoto,
    hasChanged,
  };
};

export default usePhotoUpload;
